import isEmpty from 'lodash/isEmpty'
import {
  BarChart,
  CHOROPLETH_REGION,
  CountryDataChoropleth,
  LineChart,
  RegionDataChoropleth,
  SimpleGrid
} from '~/design_system'
import { toDisplayCurrency } from '~/components/v2/visualization/PaymentsChart'
import { DashboardPageContainer, DashboardPageHeader, DEFAULT_INVOICE_STATUS } from '~/dashboards/common'
import filterAdapters from '~/dashboards/filters/adapters/spend'
import { dateRange, invoiceStatus, getBusinessUnitFilter } from '~/dashboards/filters/config'
import { getFilterParams } from '~/dashboards/filters/utils'
import { featureFlags, useFeatureFlag } from '~/hooks/useFeatureFlag'
import { useDeepEqualEffect2 } from '~/hooks/useDeepEqualEffect'
import { useVendorVisible } from '~/api/work_order'
import { usePermittedFilters } from '~/hooks/usePermittedFilters'
import { useDashboardReport, updateDashboardQueryParams, useChoroplethDataV2 } from '~/dashboards/hooks'
import { lazyMsg, useMsg } from '~/common/localizationUtils'
import { locationFilter, resourceManagerFilter, getVendorFilter } from '~/work_order_list/filtersConfig'
import { ChartCard } from '~/dashboards/ChartCard'
import { NamedQueryFilter } from '~/custom_reports/types'
import { useDashboardFilters } from './filters/hooks'

const filtersConfig = () => [
  { ...invoiceStatus, defaultValue: DEFAULT_INVOICE_STATUS },
  { ...getBusinessUnitFilter() },
  locationFilter,
  { ...resourceManagerFilter, id: 'resource_manager' },
  { ...getVendorFilter() },
  { ...dateRange, id: 'billing_cycle_end_date', title: lazyMsg('invoice_constants.billing_cycle_end_date') }
]

export const Spend = () => {
  const msg = useMsg()
  const permittedFilters = usePermittedFilters(filtersConfig())

  return (
    <>
      <DashboardPageHeader title={msg('common.ui.spend')} />
      <DashboardPageContainer filtersConfig={permittedFilters}>
        <Charts />
      </DashboardPageContainer>
    </>
  )
}

const useUpdateFiltersEffect = (filters: NamedQueryFilter[]) => {
  const last6MonthsFilters = getLast6MonthsIfNoBillingCycleFilters(filters)
  const spendByMonthFilters = getFilterParams(last6MonthsFilters, filterAdapters)
  const queryFilters = getFilterParams(filters, filterAdapters)

  const bulkDashboardQueryParams = {
    spend_by_state: { filters: queryFilters },
    spend_by_country: { filters: queryFilters },
    spend_by_vendor: { filters: queryFilters },
    spend_by_work_site: { filters: queryFilters },
    spend_by_resource_manager: { filters: queryFilters },
    spend_by_line_type: { filters: queryFilters },
    spend_by_month: { filters: spendByMonthFilters }
  }

  useDeepEqualEffect2(() => {
    updateDashboardQueryParams(bulkDashboardQueryParams)
  }, [bulkDashboardQueryParams])
}

const Charts = () => {
  const { isVendorVisible } = useVendorVisible()
  const { data: showUSMaps } = useFeatureFlag(featureFlags.SHOW_US_MAPS)
  const { data: showWorldMaps } = useFeatureFlag(featureFlags.SHOW_WORLD_MAPS)
  const businessUnitFilter = getBusinessUnitFilter()

  const filters = useDashboardFilters({
    [businessUnitFilter.id]: {
      operator: 'within_hierarchy',
      type: businessUnitFilter.type
    },
    billing_cycle_end_date_from: {
      operator: 'greater_than_or_equal',
      type: 'date_range'
    },
    billing_cycle_end_date_to: {
      operator: 'less_than_or_equal',
      type: 'date_range'
    }
  })
  useUpdateFiltersEffect(filters)

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
      {showWorldMaps && <SpendByCountry filters={filters} />}
      {showUSMaps && <SpendByState filters={filters} />}
      {isVendorVisible && <SpendByVendor filters={filters} />}
      <SpendByWorkSite filters={filters} />
      <SpendByResourceManager filters={filters} />
      <SpendByLineType filters={filters} />
      <SpendByMonth filters={filters} />
    </SimpleGrid>
  )
}

const SpendByState = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'spend_by_state'
  const queryFilters = getFilterParams(filters, filterAdapters)
  const queryData = useChoroplethDataV2({
    id: 'SpendByState',
    queryName: 'spend_by_state',
    dashboardQueryParams: { filters: queryFilters },
    format: 'currency'
  })

  return (
    <CountryDataChoropleth
      chartConfig={{
        title: msg('dashboards.chart.spend_by_state.title'),
        subTitle: msg('dashboards.chart.spend_by_state.sub_title'),
        format: 'currency'
      }}
      reportKey={reportKey}
      {...queryData}
    />
  )
}

export const SpendByCountry = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'spend_by_country'
  const queryFilters = getFilterParams(filters, filterAdapters)
  const queryData = useDashboardReport({
    id: 'SpendByCountry',
    queryName: 'spend_by_country',
    dashboardQueryParams: { filters: queryFilters },
    valueFormat: 'currency'
  })

  return (
    <RegionDataChoropleth
      region={CHOROPLETH_REGION.world}
      chartConfig={{
        title: msg('dashboards.chart.spend_by_country.title'),
        subTitle: msg('dashboards.chart.spend_by_country.sub_title'),
        format: 'currency'
      }}
      reportKey={reportKey}
      {...queryData}
    />
  )
}

const SpendByVendor = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)

  return (
    <ChartCard
      queryId="SpendByVendor"
      reportKey="spend_by_vendor"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.spend_by_vendor.title'),
        subTitle: msg('dashboards.chart.spend_by_vendor.sub_title'),
        format: 'currency'
      }}
    />
  )
}

const SpendByWorkSite = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)

  return (
    <ChartCard
      queryId="SpendByWorkSite"
      reportKey="spend_by_work_site"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.spend_by_worksite.title'),
        subTitle: msg('dashboards.chart.spend_by_worksite.sub_title'),
        format: 'currency'
      }}
    />
  )
}

const SpendByResourceManager = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)

  return (
    <ChartCard
      queryId="SpendByResourceManager"
      reportKey="spend_by_resource_manager"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.spend_by_resource_manager.title'),
        subTitle: msg('dashboards.chart.spend_by_resource_manager.sub_title'),
        format: 'currency'
      }}
    />
  )
}

export const SpendByLineType = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)

  return (
    <ChartCard
      queryId="SpendByLineType"
      reportKey="spend_by_line_type"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.spend_by_line_type.title'),
        subTitle: msg('dashboards.chart.spend_by_line_type.sub_title'),
        format: 'currency'
      }}
    />
  )
}

export const getLast6MonthsIfNoBillingCycleFilters = (filters: NamedQueryFilter[]): NamedQueryFilter[] => {
  const hasBillingCycleFilters = !isEmpty(
    filters.filter(
      _filter => _filter.id === 'billing_cycle_end_date_from' || _filter.id === 'billing_cycle_end_date_to'
    )
  )
  return hasBillingCycleFilters ? filters : [...filters, { id: 'billing_cycle_end_date', operator: 'last_6_months' }]
}

export const SpendByMonth = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()

  const _filters = getLast6MonthsIfNoBillingCycleFilters(filters)
  const queryFilters = getFilterParams(_filters, filterAdapters)
  useDeepEqualEffect2(() => {
    updateDashboardQueryParams({ spend_by_month: { filters: queryFilters } })
  }, [queryFilters])

  return (
    <ChartCard
      queryId="SpendByMonth"
      reportKey="spend_by_month"
      filters={queryFilters}
      chart={LineChart}
      chartConfig={{
        format: 'currency',
        xAxisLabel: msg('dashboards.chart.spend_by_month.x_axis_label'),
        yAxisLabel: msg('dashboards.chart.spend_by_month.y_axis_label'),
        title: msg('dashboards.chart.spend_by_month.title'),
        subTitle: msg('dashboards.chart.spend_by_month.sub_title'),
        yAxisFormat: toDisplayCurrency
      }}
    />
  )
}
